import React, { useState } from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, Moon, Sun, X, Zap } from "lucide-react";
import { SeoHead } from "./components/SeoHead";
import { smartLangPath } from "./components/ContextualLinks";
import { FORMAT_HUBS } from "./content/formats";
import { PROVIDER_LIMITS } from "./content/limits";
import { ARTICLE_HUBS } from "./content/personas";
import { LANGUAGES, UiProvider, buildLangPath, detectLangFromPath, useUi } from "./i18n";
import { ArticlePage } from "./pages/ArticlePage";
import { Faq, HowItWorks } from "./pages/Content";
import { Download } from "./pages/Download";
import { FormatPage } from "./pages/FormatPage";
import { AttachmentLimits, EncryptedTransfer, SendAnonymously, SendLargeFiles, WeTransferAlternative } from "./pages/Guides";
import { Home } from "./pages/Home";
import { Privacy, Security, Terms } from "./pages/Legal";
import { LimeDrop } from "./pages/LimeDrop";
import { LimitsHub } from "./pages/LimitsHub";
import { ProductExceed, ProductExtend, ProductTeams } from "./pages/ProductPages";
import { ReceiptVerify } from "./pages/ReceiptVerify";
import { RequestFilesPage } from "./pages/RequestFiles";
import { EncryptFile } from "./pages/tools/EncryptFile";
import { FileChecksum } from "./pages/tools/FileChecksum";
import { UploadTimeCalculator } from "./pages/tools/UploadTimeCalculator";
import { UltimateGuide } from "./pages/UltimateGuide";
import { VideoReviewPage } from "./pages/VideoReviewPage";
import { DevelopersPage, SellFilesPage } from "./pages/DevelopersPage";
import { OpenDataHubPage, PapersHubPage, ResearchHubPage } from "./pages/AcademicPages";
import { AcademicHub } from "./pages/AcademicHub";

export function App() {
  const loc = useLocation();
  const detected = detectLangFromPath(loc.pathname).lang;
  return (
    <UiProvider {...(detected ? { initialLang: detected } : {})}>
      <AppInner />
    </UiProvider>
  );
}

function AppInner() {
  const { lang, setLang, t, theme, toggleTheme } = useUi();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { lang: urlLang, strippedPath } = detectLangFromPath(location.pathname);

  React.useEffect(() => {
    setMobileMenuOpen(false);
    setLangOpen(false);
    if (urlLang && urlLang !== lang) {
      setLang(urlLang);
    }
  }, [location.pathname, urlLang]);

  const currentLang = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0]!;

  return (
    <div className="shell">
      <SeoHead />

      {/* Enterprise Header (Dropdowns + 12-Language Popover + Theme Switcher) */}
      <header className="nav">
        <Link to={buildLangPath("/", lang)} className="brand" aria-label="LimeShare Home">
          <span className="dot" />
          LIME<span className="accent">SHARE</span>
        </Link>

        {/* Center Navigation Dropdowns */}
        <nav className="nav-center">
          <div className="nav-dropdown">
            <button type="button" className="nav-dropdown-trigger">
              {t.navProducts} <ChevronDown size={14} />
            </button>
            <div className="nav-dropdown-menu">
              <Link to="/products/teams">LimeShare Teams (25 Seats)</Link>
              <Link to="/products/exceed">LimeShare Exceed (Up to 1 TB)</Link>
              <Link to="/products/extend">LimeShare Extend (1 Year)</Link>
              <Link to="/request-files">Receive Files Portal (Reverse Drop)</Link>
              <Link to="/video-review-tool">Frame-Accurate Video Review</Link>
              <Link to="/drop">LimeDrop (Direct WebRTC P2P)</Link>
            </div>
          </div>

          <div className="nav-dropdown">
            <button type="button" className="nav-dropdown-trigger">
              {t.navSolutions} <ChevronDown size={14} />
            </button>
            <div className="nav-dropdown-menu">
              <Link to="/for-videographers">For Videographers &amp; Studios</Link>
              <Link to="/for-architects">For Architects &amp; BIM</Link>
              <Link to="/for-lawyers">For Lawyers &amp; Legal</Link>
              <Link to="/for-photographers">For Photographers</Link>
              <Link to="/for-musicians-podcasters">For Musicians &amp; Audio</Link>
              <Link to="/for-game-developers">For Game Developers</Link>
              <Link to="/for-healthcare">For Healthcare &amp; DICOM</Link>
              <Link to="/share-database-backups">For DevOps &amp; SQL Backups</Link>
            </div>
          </div>

          <div className="nav-dropdown">
            <button type="button" className="nav-dropdown-trigger">
              {t.navTools} <ChevronDown size={14} />
            </button>
            <div className="nav-dropdown-menu">
              <Link to="/request-files">Request Files from Clients</Link>
              <Link to="/video-review-tool">Video &amp; Audio Review Player</Link>
              <Link to="/tools/encrypt-file">Offline File Encryptor (.lsc)</Link>
              <Link to="/tools/upload-time-calculator">Upload Speed Calculator</Link>
              <Link to="/tools/file-checksum">SHA-256 Checksum Verifier</Link>
              <Link to="/verify-receipt">Certified Destruction Verifier</Link>
            </div>
          </div>

          <div className="nav-dropdown">
            <button type="button" className="nav-dropdown-trigger">
              {t.navSupport} <ChevronDown size={14} />
            </button>
            <div className="nav-dropdown-menu">
              <Link to="/research">Academic Research &amp; Harvard / CERN DOIs</Link>
              <Link to="/papers">PDF Working Papers &amp; Compendiums</Link>
              <Link to="/data">Open Benchmark Dataset (CSV / JSONL)</Link>
              <Link to="/ultimate-file-transfer-guide">2026 Master Transfer Guide</Link>
              <Link to="/send-large-files">All File &amp; App Guides</Link>
              <Link to="/how-it-works">How It Works</Link>
              <Link to="/faq">FAQ</Link>
              <Link to="/security">Security Architecture</Link>
            </div>
          </div>
        </nav>

        {/* Right Controls: 12-Language Grid Popover + Light/Dark Theme Switcher + CTA */}
        <div className="nav-right">
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="btn ghost btn-sm"
              style={{ borderRadius: 999, padding: "7px 12px" }}
              onClick={() => setLangOpen(!langOpen)}
            >
              <span>{currentLang.flag}</span>
              <span>{currentLang.label}</span>
              <ChevronDown size={13} />
            </button>

            {langOpen && (
              <div className="lang-popover">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    className={`lang-btn-item${l.code === lang ? " active" : ""}`}
                    onClick={() => {
                      setLang(l.code);
                      setLangOpen(false);
                      navigate(buildLangPath(strippedPath, l.code));
                    }}
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={theme === "light" ? "Switch to Dark Theme" : "Switch to Light Theme"}
            aria-label="Toggle color theme"
          >
            {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </button>

          <Link to="/products/exceed" className="btn btn-sm nav-send-1tb-cta" style={{ borderRadius: 999, padding: "8px 14px" }}>
            <Zap size={13} /> {t.send1Tb}
          </Link>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="mobile-drawer" role="dialog" aria-modal="true">
          <div className="mobile-drawer-header">
            <Link to={buildLangPath("/", lang)} className="brand" onClick={() => setMobileMenuOpen(false)}>
              <span className="dot" />
              LIME<span className="accent">SHARE</span>
            </Link>
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
              <X size={24} />
            </button>
          </div>

          <div className="mobile-drawer-section">
            <div className="mobile-drawer-title">Products &amp; Transfer Modes</div>
            <div className="mobile-drawer-links">
              <Link to={buildLangPath("/", lang)}>Free Encrypted Drop (Up to 5 GB)</Link>
              <Link to="/products/teams">LimeShare Teams (25 People · $149/yr)</Link>
              <Link to="/products/exceed">LimeShare Exceed (Up to 1 TB)</Link>
              <Link to="/products/extend">LimeShare Extend (Keep 1 Year)</Link>
              <Link to="/request-files">Receive Files Portal (Reverse Drop)</Link>
              <Link to="/video-review-tool">Frame-Accurate Video Review</Link>
              <Link to="/drop">LimeDrop (Direct WebRTC P2P)</Link>
            </div>
          </div>

          <div className="mobile-drawer-section">
            <div className="mobile-drawer-title">Free Tools</div>
            <div className="mobile-drawer-links">
              <Link to="/tools/encrypt-file">Offline File Encryptor (.lsc)</Link>
              <Link to="/tools/upload-time-calculator">Speed &amp; ETA Calculator</Link>
              <Link to="/tools/file-checksum">SHA-256 Checksum Verifier</Link>
              <Link to="/verify-receipt">Certified Destruction Verifier</Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Router (Language-Aware via strippedPath) */}
      <main>
        <Routes location={{ ...location, pathname: strippedPath }}>
          <Route path="/" element={<Home />} />
          <Route path="/d/:id" element={<Download />} />
          <Route path="/f/:id/:fileId" element={<Download />} />
          <Route path="/drop" element={<LimeDrop />} />
          <Route path="/drop/:roomId" element={<LimeDrop />} />
          <Route path="/request-files" element={<RequestFilesPage />} />
          <Route path="/r/:slug" element={<RequestFilesPage />} />
          <Route path="/embed/r/:slug" element={<RequestFilesPage />} />
          <Route path="/video-review-tool" element={<VideoReviewPage />} />
          <Route path="/developers" element={<DevelopersPage />} />
          <Route path="/sell-digital-files" element={<SellFilesPage />} />
          <Route path="/research" element={<ResearchHubPage />} />
          <Route path="/papers" element={<PapersHubPage />} />
          <Route path="/data" element={<OpenDataHubPage />} />
          <Route path="/citation" element={<AcademicHub />} />
          <Route path="/products/teams" element={<ProductTeams />} />
          <Route path="/products/exceed" element={<ProductExceed />} />
          <Route path="/products/extend" element={<ProductExtend />} />
          <Route path="/ultimate-file-transfer-guide" element={<UltimateGuide />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/send-large-files" element={<SendLargeFiles />} />
          <Route path="/email-attachment-size-limits" element={<AttachmentLimits />} />
          <Route path="/wetransfer-alternative" element={<WeTransferAlternative />} />
          <Route path="/encrypted-file-transfer" element={<EncryptedTransfer />} />
          <Route path="/send-files-anonymously" element={<SendAnonymously />} />

          {/* Interactive Tools */}
          <Route path="/tools/encrypt-file" element={<EncryptFile />} />
          <Route path="/tools/upload-time-calculator" element={<UploadTimeCalculator />} />
          <Route path="/tools/file-checksum" element={<FileChecksum />} />

          {/* Certified Destruction Verifier */}
          <Route path="/verify-receipt" element={<ReceiptVerify />} />
          <Route path="/receipt/:id" element={<ReceiptVerify />} />

          {/* Programmatic Format Hubs */}
          {FORMAT_HUBS.map((hub) => (
            <Route key={hub.slug} path={`/${hub.slug}`} element={<FormatPage hub={hub} />} />
          ))}

          {/* Programmatic Provider Limits Hubs */}
          {PROVIDER_LIMITS.map((item) => (
            <Route key={item.slug} path={`/${item.slug}`} element={<LimitsHub item={item} />} />
          ))}

          {/* Programmatic Article Hubs */}
          {ARTICLE_HUBS.map((hub) => (
            <Route key={hub.slug} path={`/${hub.slug}`} element={<ArticlePage hub={hub} />} />
          ))}

          <Route path="/security" element={<Security />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route
            path="*"
            element={
              <section className="panel">
                <h2>Page not found</h2>
                <p className="hint">
                  <Link to="/">Back to LimeShare</Link>
                </p>
              </section>
            }
          />
        </Routes>
      </main>

      {/* Theme-Adaptive Enterprise Footer */}
      <footer className="footer-enterprise">
        <div className="footer-inner">
          <div className="footer-grid">
            <div>
              <div className="footer-col-title">{t.navProducts} &amp; {t.navTools}</div>
              <ul className="footer-links-list">
                <li><Link to="/products/teams">LimeShare Teams (25 Seats)</Link></li>
                <li><Link to="/products/exceed">LimeShare Exceed (Up to 1 TB)</Link></li>
                <li><Link to="/products/extend">LimeShare Extend (1 Year)</Link></li>
                <li><Link to={smartLangPath("/request-files", lang)}>Receive Files Portal (Reverse Drop)</Link></li>
                <li><Link to={smartLangPath("/video-review-tool", lang)}>Frame-Accurate Video Review</Link></li>
                <li><Link to="/sell-digital-files">Sell Files (Pay-to-Decrypt Escrow)</Link></li>
                <li><Link to="/developers">Developers &amp; Terminal CLI</Link></li>
                <li><Link to="/research">Academic Research &amp; Harvard / CERN DOIs</Link></li>
                <li><Link to="/papers">Working Papers &amp; PDF Compendiums</Link></li>
                <li><Link to="/data">Open Benchmark Dataset (CSV / JSONL)</Link></li>
                <li><Link to="/citation">BibTeX / APA Citation Generator</Link></li>
                <li><Link to="/drop">LimeDrop (Direct P2P)</Link></li>
                <li><Link to="/tools/encrypt-file">Offline File Encryptor</Link></li>
                <li><Link to="/tools/upload-time-calculator">Speed Calculator</Link></li>
                <li><Link to="/tools/file-checksum">SHA-256 Checksum Verifier</Link></li>
                <li><Link to="/verify-receipt">Certified Destruction Verifier</Link></li>
              </ul>
            </div>

            <div>
              <div className="footer-col-title">File Formats</div>
              <ul className="footer-links-list">
                {FORMAT_HUBS.map((h) => (
                  <li key={h.slug}>
                    <Link to={smartLangPath(`/${h.slug}`, lang)}>{h.formatName}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="footer-col-title">Platform Limits</div>
              <ul className="footer-links-list">
                <li><Link to="/email-attachment-size-limits">All Email &amp; Chat Limits</Link></li>
                {PROVIDER_LIMITS.map((p) => (
                  <li key={p.slug}>
                    <Link to={smartLangPath(`/${p.slug}`, lang)}>{p.provider}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="footer-col-title">Comparisons &amp; Guides</div>
              <ul className="footer-links-list">
                <li><Link to="/ultimate-file-transfer-guide">Ultimate 2026 Transfer Guide</Link></li>
                <li><Link to="/send-large-files">How to Send Large Files</Link></li>
                <li><Link to="/encrypted-file-transfer">Encrypted Transfer Explained</Link></li>
                <li><Link to="/send-files-anonymously">Anonymous File Sharing</Link></li>
                <li><Link to={smartLangPath("/wetransfer-alternative", lang)}>WeTransfer Alternative</Link></li>
                {ARTICLE_HUBS.filter((a) => a.category === "alternative").map((a) => (
                  <li key={a.slug}>
                    <Link to={smartLangPath(`/${a.slug}`, lang)}>{a.h1.slice(0, 32)}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="footer-col-title">Solutions &amp; Sizes</div>
              <ul className="footer-links-list">
                {ARTICLE_HUBS.filter((a) => a.category !== "alternative").map((a) => (
                  <li key={a.slug}>
                    <Link to={smartLangPath(`/${a.slug}`, lang)}>{a.h1.slice(0, 32)}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Crawlable 12-Language Switcher Bar */}
          <div style={{ padding: "14px 0", borderTop: "1px solid var(--border)", display: "flex", gap: "8px 14px", flexWrap: "wrap", alignItems: "center", fontSize: 12 }}>
            <b style={{ color: "var(--text)" }}>Languages:</b>
            {LANGUAGES.map((l) => (
              <Link
                key={l.code}
                to={smartLangPath(strippedPath, l.code)}
                onClick={() => setLang(l.code)}
                style={{ color: l.code === lang ? "var(--lime)" : "var(--muted)", fontWeight: l.code === lang ? 700 : 400 }}
              >
                {l.flag} {l.nativeName} ({l.label})
              </Link>
            ))}
          </div>

          <div className="footer-bottom">
            <div>
              <b style={{ color: "var(--text)", marginRight: 10 }}>LIMESHARE</b>
              <span>Send large files free, zero-knowledge encrypted, and fast · 2026 © All rights reserved</span>
            </div>
            <div className="footer-legal-links">
              <Link to="/ultimate-file-transfer-guide">2026 Guide</Link>
              <Link to="/how-it-works">How it works</Link>
              <Link to="/faq">FAQ</Link>
              <Link to="/security">Security</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/privacy">Privacy</Link>
              <a href="/llms.txt">llms.txt</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
